<script>
  import { onMount } from 'svelte';
  import { fly, fade } from 'svelte/transition';
  import PeopleIcon from './icons/PeopleIcon.svelte';
  import CopyIcon from './icons/CopyIcon.svelte';
  import { createEventDispatcher } from 'svelte';

  export let authData; // Получаем от родительского компонента

  const dispatch = createEventDispatcher();
  let loading = true;
  let error = null;
  let referralData = null; // { link: string, description: string, friends: Array<{name: string, status: 'pending' | 'active'}>, totalAwardedSpins: number }
  let copied = false;

  async function fetchReferralData() {
    loading = true;
    error = null;
    
    try {
      const response = await fetch('/api/v1/referrals', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Отправляем данные аутентификации для бэкенда
          'X-Telegram-Auth': authData
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      referralData = await response.json();
      console.log(response)
    } catch (err) {
      console.error("Failed to load referral data:", err);
      error = "Не удалось загрузить данные рефералов.";
      // Можно отправить событие об ошибке наверх
      dispatch('error', { message: error });
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    if (authData) { // Загружаем данные только если есть данные аутентификации
        fetchReferralData();
    } else {
        error = "Не удалось получить данные аутентификации Telegram.";
        loading = false;
        console.warn("Auth data is missing, cannot fetch referrals.");
    }
  });

  async function copyLink() {
    if (!referralData?.link || copied) return;
    try {
      await navigator.clipboard.writeText(referralData.link);
      copied = true;
      setTimeout(() => copied = false, 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
      // Можно показать временное сообщение об ошибке копирования
      dispatch('notify', { message: 'Не удалось скопировать ссылку', type: 'error' });
    }
  }
</script>

<div class="tab-content-container">
  {#if loading}
    <div class="loading-placeholder">
      <div class="spinner large"></div>
      <p>Загружаем данные друзей...</p>
    </div>
  {:else if error}
    <div class="error-message" transition:fade>
      <p>{error}</p>
      <button class="retry-button small" on:click={() => window.location.reload()}>Попробовать снова</button>
    </div>
  {:else if referralData}
    <div class="friends-content" in:fly={{ y: 20, duration: 400 }}>
      <div class="card referral-info">
        <PeopleIcon size="40"/>
        <h2>Пригласи друзей</h2>
        <p class="description" transition:fade|local> {@html referralData.description} </p>
      </div>

      <div class="card referral-link-section" transition:fade|local={{delay: 100}}>
        <h3>Ваша реферальная ссылка:</h3>
        <div class="link-container">
          <input type="text" readonly value={referralData.link} />
          <button class="copy-button" on:click={copyLink} class:copied disabled={copied}>
            <CopyIcon />
            <span>{copied ? 'Скопировано!' : 'Копировать'}</span>
          </button>
        </div>
      </div>

      <div class="card friends-list-section" transition:fade|local={{delay: 200}}>
        <h3>Приглашенные друзья ({referralData.friends.length})</h3>
        {#if referralData.friends.length > 0}
          <ul class="friends-list">
            {#each referralData.friends as friend, i (friend.name)}
              <li in:fly={{ y: 10, duration: 300, delay: i * 50 }}>
                <span class="friend-name">{friend.name}</span>
                <span class="friend-status {friend.status}">
                  {friend.status === 'active' ? 'Активен ✅' : 'Ожидание ⏳'}
                </span>
              </li>
            {/each}
          </ul>
          <p class="total-reward">Всего получено прокрутов: <span class="spin-highlight">{referralData.totalSpins}</span></p>
        {:else}
          <p class="no-friends">Вы еще никого не пригласили. Поделитесь ссылкой!</p>
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  /* Общие стили для вкладок (можно вынести) */
  .tab-content-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start; /* Начинаем сверху */
    padding: 15px;
    box-sizing: border-box;
    gap: 15px;
    overflow-y: auto; /* Скролл если много друзей */
  }

  .loading-placeholder, .error-message {
    margin-top: 30px;
    text-align: center;
    color: #A6C5E1;
  }
   .spinner.large { width: 40px; height: 40px; border-width: 4px; margin: 0 auto 15px; }
   /* --- Стили спиннера (можно взять из MainScreen) --- */
  .spinner { border: 3px solid rgba(135, 195, 255, 0.2); border-top-color: #87C3FF; border-radius: 50%; animation: spin 1s linear infinite; display: inline-block; }
  @keyframes spin { to { transform: rotate(360deg); } }

  .error-message button { margin-top: 10px; }
  .retry-button.small { padding: 6px 15px; font-size: 0.9rem; }

  /* --- Стили для контента Друзей --- */
  .friends-content {
    width: 100%;
    max-width: 450px;
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .card {
    background-color: rgba(20, 30, 42, 0.8); /* Полупрозрачный фон карточек */
    backdrop-filter: blur(5px);
    border-radius: 12px;
    padding: 15px 20px;
    border: 1px solid rgba(110, 191, 255, 0.08);
    box-shadow: 0 4px 15px rgba(0,0,0,0.2);
    text-align: center;
  }

  .referral-info h2 {
    margin: 10px 0 8px;
    font-size: 1.3rem;
    font-weight: 700;
    color: #E1E9F0;
  }
  .referral-info .description {
    color: #A6C5E1;
    line-height: 1.5;
    margin-bottom: 0;
    font-size: 0.95rem;
  }
  .referral-info :global(strong) {
     color: #87C3FF;
     font-weight: 700;
  }
  .referral-info :global(svg) { /* Иконка вверху */
     fill: #6EBFFF;
     opacity: 0.7;
     margin-bottom: 5px;
     width: 35px;
     height: 35px;
  }

  .referral-link-section h3 {
    margin: 0 0 12px;
    font-size: 1rem;
    font-weight: 600;
    color: #A6C5E1;
  }

  .link-container {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }
  .link-container input[type="text"] {
    flex-grow: 1;
    padding: 8px 12px;
    background-color: rgba(10, 15, 20, 0.7);
    border: 1px solid rgba(110, 191, 255, 0.15);
    border-radius: 8px;
    color: #E1E9F0;
    font-size: 0.9rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .copy-button {
    padding: 8px 12px;
    background: linear-gradient(45deg, #4A90E2, #61A6FF);
    border: none;
    border-radius: 8px;
    color: white;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.3s ease;
    flex-shrink: 0; /* Не сжиматься */
  }
  .copy-button:hover:not(:disabled) { filter: brightness(1.1); transform: scale(1.03); }
  .copy-button :global(svg) { width: 16px; height: 16px; fill: currentColor; }
  .copy-button.copied {
    background: linear-gradient(45deg, #4CAF50, #81C784); /* Зеленый при копировании */
    cursor: default;
  }
  .copy-button span { font-size: 0.9rem; }

  .friends-list-section h3 {
     margin: 0 0 15px;
     font-size: 1.1rem;
     font-weight: 600;
     color: #C0D3E6;
     text-align: left;
  }
  .friends-list {
    list-style: none;
    padding: 0;
    margin: 0 0 15px;
    max-height: 150px; /* Ограничим высоту списка */
    overflow-y: auto; /* Скролл для списка */
    text-align: left;
  }
  .friends-list li {
    display: flex;
    justify-content: space-between;
    padding: 8px 5px;
    border-bottom: 1px solid rgba(110, 191, 255, 0.08);
    font-size: 0.95rem;
  }
   .friends-list li:last-child { border-bottom: none; }
  .friend-name { color: #E1E9F0; }
  .friend-status { font-weight: 600; font-size: 0.9rem; }
  .friend-status.active { color: #81C784; } /* Зеленый */
  .friend-status.pending { color: #FFB74D; } /* Оранжевый */

  .no-friends, .total-reward {
    color: #A6C5E1;
    font-size: 0.95rem;
    text-align: center;
    margin-top: 10px;
  }
  .total-reward { text-align: right; font-weight: 600; }
  .spin-highlight { color: #87C3FF; font-size: 1.1em; }

</style>